import { AnalyticsEvents, type ByoMediaUpdatedProperties } from '@mondaygpt/analytics-events'
import { BYO_MAX_CAROUSEL_ITEMS, isUserMediaContent } from '@mondaygpt/types'
import { cn } from 'lib/utils'
import { ChevronLeft, ChevronRight, Loader2, Plus, RefreshCw, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { ulid } from 'ulid'
import { useAnalytics } from 'hooks'
import { apiCall } from 'modules/core/services/api/api'
import { MediaService } from 'openapi/services/MediaService'
import { ProjectService } from 'openapi/services/ProjectService'
import { useIdeaStore } from '../store/idea-store'
import { flushPersistsAndWait, useCreativeStore } from '../store/creative-store'
import type { Creative } from '../types'
import {
  BYO_ACCEPT_ATTR,
  BYO_IMAGE_TYPES,
  BYO_VIDEO_TYPES,
  byoFilesError,
} from '../utils/byo-media'
import { extractApiError } from '../utils/extract-api-error'

type TrayItem = { attachmentId: string; url: string | null; kind: 'video' | 'image' }
type TrayAction = ByoMediaUpdatedProperties['action']

/** The current media of a BYO creative, in slide order, straight off content. */
function trayItemsOf(content: Record<string, unknown>): TrayItem[] {
  if (typeof content.videoAttachmentId === 'string') {
    return [
      {
        attachmentId: content.videoAttachmentId,
        url: typeof content.videoUrl === 'string' ? content.videoUrl : null,
        kind: 'video',
      },
    ]
  }
  if (Array.isArray(content.mediaItems)) {
    return (content.mediaItems as Array<Record<string, unknown>>)
      .filter(item => typeof item.attachmentId === 'string')
      .map(item => ({
        attachmentId: item.attachmentId as string,
        url: typeof item.url === 'string' ? item.url : null,
        kind: item.type === 'video' ? 'video' : 'image',
      }))
  }
  if (typeof content.imageAttachmentId === 'string') {
    return [
      {
        attachmentId: content.imageAttachmentId,
        url: typeof content.imageUrl === 'string' ? content.imageUrl : null,
        kind: 'image',
      },
    ]
  }
  return []
}

/**
 * The user's hands on their OWN pixels: swap, reorder, add or remove the files
 * of a bring-your-own-content post, right under the composer preview. The
 * untouched guarantee is about Taka's hands — Taka still never modifies these
 * files; this tray only changes WHICH files the user is publishing.
 *
 * One PUT swaps the media on every platform creative of the post (they are
 * pinned clones of the same upload); per-platform words survive untouched.
 */
export function ByoMediaTray({ creative, className }: { creative: Creative; className?: string }) {
  const { track } = useAnalytics()
  const loadIdea = useIdeaStore(s => s.loadIdea)
  const projectId = creative.projectId
  const anyPublished = useCreativeStore(s =>
    s.creatives.some(c => c.projectId === projectId && !!c.publishedAt)
  )

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // 'add' appends photos; 'swap' replaces the whole set (the only way out of a
  // single file or a video, which never coexists with anything else).
  const pickerModeRef = useRef<Extract<TrayAction, 'add' | 'swap'>>('add')

  const content = creative.content as unknown as Record<string, unknown>
  const items = trayItemsOf(content)
  const isVideoPost = items.some(i => i.kind === 'video')
  const canAdd = !isVideoPost && items.length < BYO_MAX_CAROUSEL_ITEMS

  if (!isUserMediaContent(content) || !projectId || items.length === 0 || anyPublished) {
    return null
  }

  const commit = async (attachmentIds: string[], action: TrayAction, hasVideo: boolean) => {
    setBusy(true)
    setError(null)
    try {
      // Land any pending debounced caption/content PUTs FIRST (same ritual as
      // confirmIdea): a stale full-content persist firing mid-swap would
      // revert the media server-side, and the loadIdea fold below would
      // otherwise clobber the un-persisted edit locally.
      const projectCreativeIds = useCreativeStore
        .getState()
        .creatives.filter(c => c.projectId === projectId)
        .map(c => c.id)
      await flushPersistsAndWait(projectCreativeIds)
      await apiCall(ProjectService.projectControllerUpdateUserMediaV1, null, projectId, {
        attachmentIds,
      })
      // The PUT response carries unresolved content (no signed urls) — the
      // resolving project read is the canonical refresh for every surface.
      await loadIdea(projectId)
      track(AnalyticsEvents.Posts.BYO_MEDIA_UPDATED, {
        media_kind: hasVideo ? 'video' : attachmentIds.length >= 2 ? 'carousel' : 'image',
        file_count: attachmentIds.length,
        action,
      } satisfies ByoMediaUpdatedProperties)
    } catch (err) {
      console.error('BYO media update failed:', err)
      setError(extractApiError(err, "We couldn't update the media. Please try again."))
    } finally {
      setBusy(false)
    }
  }

  const uploadAndCommit = async (files: File[]) => {
    const mode = pickerModeRef.current
    if (files.length === 0) return
    if (mode === 'add') {
      const nonImage = files.find(f => !(BYO_IMAGE_TYPES as readonly string[]).includes(f.type))
      if (nonImage) {
        setError('Only photos can join this post. To switch to a video, swap the files instead.')
        return
      }
      // The shared BYO rules (per-file size caps + count) over the ADDED
      // photos — same helper as every other BYO door, so the rules can't
      // drift per surface.
      const addError = byoFilesError(files)
      if (addError) {
        setError(addError)
        return
      }
      if (items.length + files.length > BYO_MAX_CAROUSEL_ITEMS) {
        setError(`Up to ${BYO_MAX_CAROUSEL_ITEMS} images per post.`)
        return
      }
    } else {
      const mixError = byoFilesError(files)
      if (mixError) {
        setError(mixError)
        return
      }
    }
    setBusy(true)
    setError(null)
    try {
      const uploaded = await Promise.all(
        files.map(file =>
          apiCall(MediaService.mediaControllerUploadFileV1, null, { file, id: ulid() })
        )
      )
      const newIds = uploaded.map(a => a.id)
      const nextIds = mode === 'add' ? [...items.map(i => i.attachmentId), ...newIds] : newIds
      const hasVideo =
        mode === 'swap' && files.some(f => (BYO_VIDEO_TYPES as readonly string[]).includes(f.type))
      await commit(nextIds, mode, hasVideo)
    } catch (err) {
      console.error('BYO media upload failed:', err)
      setError(extractApiError(err, "We couldn't upload the files. Please try again."))
      setBusy(false)
    }
  }

  const openPicker = (mode: Extract<TrayAction, 'add' | 'swap'>) => {
    pickerModeRef.current = mode
    const input = fileInputRef.current
    if (!input) return
    // Imperative accept: mode and filter change together, no state/raf dance.
    input.accept = mode === 'add' ? BYO_IMAGE_TYPES.join(',') : BYO_ACCEPT_ATTR
    input.click()
  }

  const removeAt = (index: number) => {
    const next = items.filter((_, i) => i !== index).map(i => i.attachmentId)
    void commit(next, 'remove', false)
  }

  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta
    if (target < 0 || target >= items.length) return
    const ids = items.map(i => i.attachmentId)
    ;[ids[index], ids[target]] = [ids[target], ids[index]]
    void commit(ids, 'reorder', isVideoPost)
  }

  return (
    <div className={className}>
      <div className="mb-1 flex items-center gap-2">
        <span className="text-[11px] font-medium tracking-wide text-paper-faint uppercase">
          Your files
        </span>
        <span className="text-[11px] text-paper-faint/70">published exactly as uploaded</span>
        {busy && <Loader2 size={12} className="animate-spin text-paper-faint" aria-hidden />}
      </div>
      <div
        className={cn(
          'flex flex-wrap items-center gap-2 rounded-[9px] border border-dashed border-line px-2.5 py-2.5 transition-colors',
          dragOver && 'border-acid/60 bg-acid/5'
        )}
        onDragOver={e => {
          // ALWAYS prevent the default first — an unprevented file drop
          // navigates the tab to the file and unloads the composer. Gate the
          // ACTION, never the prevention.
          e.preventDefault()
          if (!canAdd || busy) return
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault()
          setDragOver(false)
          if (!canAdd || busy) return
          pickerModeRef.current = 'add'
          void uploadAndCommit(Array.from(e.dataTransfer.files))
        }}
      >
        {items.map((item, index) => (
          <span key={item.attachmentId} className="group/thumb relative">
            {item.kind === 'video' ? (
              <video
                src={item.url ?? undefined}
                muted
                playsInline
                preload="metadata"
                className="aspect-[4/5] w-14 rounded-[8px] border border-line object-cover"
              />
            ) : (
              <img
                src={item.url ?? undefined}
                alt={`File ${index + 1}`}
                className="aspect-[4/5] w-14 rounded-[8px] border border-line object-cover"
              />
            )}
            {!busy && items.length > 1 && (
              <button
                type="button"
                onClick={() => removeAt(index)}
                aria-label={`Remove file ${index + 1}`}
                className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full border border-line bg-bg-2 text-paper-faint transition-colors hover:text-paper"
              >
                <X size={10} />
              </button>
            )}
            {!busy && items.length > 1 && (
              <span className="absolute inset-x-0 bottom-0.5 flex justify-center gap-0.5 opacity-0 transition-opacity group-hover/thumb:opacity-100">
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={`Move file ${index + 1} left`}
                  className="flex h-4 w-4 items-center justify-center rounded-full bg-bg/80 text-paper backdrop-blur disabled:opacity-30"
                >
                  <ChevronLeft size={10} />
                </button>
                <button
                  type="button"
                  disabled={index === items.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={`Move file ${index + 1} right`}
                  className="flex h-4 w-4 items-center justify-center rounded-full bg-bg/80 text-paper backdrop-blur disabled:opacity-30"
                >
                  <ChevronRight size={10} />
                </button>
              </span>
            )}
          </span>
        ))}
        {canAdd && (
          <button
            type="button"
            disabled={busy}
            onClick={() => openPicker('add')}
            aria-label="Add more photos"
            className="flex aspect-[4/5] w-14 items-center justify-center rounded-[8px] border border-dashed border-line text-paper-faint transition-colors hover:border-acid/50 hover:text-acid"
          >
            <Plus size={12} />
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={() => openPicker('swap')}
          className="ml-auto flex items-center gap-1 self-center px-1 text-[11px] text-paper-faint transition-colors hover:text-paper"
        >
          <RefreshCw size={10} />
          Swap files
        </button>
      </div>
      {error && (
        <p className="mt-1.5 text-[12px] leading-snug text-[color:var(--alert-error-text)]">
          {error}
        </p>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={BYO_IMAGE_TYPES.join(',')}
        multiple
        className="hidden"
        onChange={e => {
          const picked = Array.from(e.target.files ?? [])
          e.target.value = ''
          void uploadAndCommit(picked)
        }}
      />
    </div>
  )
}
